import { useEffect } from "react";
import { Switch, Route, Redirect, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { TestProvider } from "@/context/TestContext";
import { DevModeProvider } from "@/context/DevModeContext";
import { UpgradeProvider } from "@/context/UpgradeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RequireAiActions } from "@/components/RequireAiActions";
import { DevOptions } from "@/components/DevOptions";
import { ErrorBoundary } from "@/components/ErrorBoundary";

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  return null;
}

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Settings from "@/pages/Settings";
import MyTests from "@/pages/MyTests";
import IdentifyContent from "@/pages/IdentifyContent";
import BuildBlueprint from "@/pages/BuildBlueprint";
import ReviewBlueprint from "@/pages/ReviewBlueprint";
import GenerateTest from "@/pages/GenerateTest";
import ReviewTest from "@/pages/ReviewTest";
import FinalizeTest from "@/pages/FinalizeTest";
import ShareTest from "@/pages/ShareTest";
import TestPreview from "@/pages/TestPreview";
import EditItems from "@/pages/EditItems";
import NotFound from "@/pages/not-found";

// Dev-only: visit /__crash to see the ErrorBoundary page. Not registered in production builds.
function CrashTest(): never {
  throw new Error("Test crash from /__crash");
}

function Router() {
  return (
    <Switch>
      <Route path="/" component={Login} />
      <Route path="/dashboard">
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      </Route>
      <Route path="/tests">
        <ProtectedRoute>
          <MyTests />
        </ProtectedRoute>
      </Route>
      <Route path="/account">
        <ProtectedRoute>
          <Redirect to="/settings/billing" replace />
        </ProtectedRoute>
      </Route>
      <Route path="/settings">
        <ProtectedRoute>
          <Redirect to="/settings/profile" replace />
        </ProtectedRoute>
      </Route>
      <Route path="/settings/:section">
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      </Route>
      <Route path="/create/identify">
        <ProtectedRoute>
          <RequireAiActions>
            <IdentifyContent />
          </RequireAiActions>
        </ProtectedRoute>
      </Route>
      <Route path="/create/blueprint">
        <ProtectedRoute>
          <RequireAiActions>
            <BuildBlueprint />
          </RequireAiActions>
        </ProtectedRoute>
      </Route>
      <Route path="/create/review-blueprint">
        <ProtectedRoute>
          <RequireAiActions>
            <ReviewBlueprint />
          </RequireAiActions>
        </ProtectedRoute>
      </Route>
      <Route path="/create/generate">
        <ProtectedRoute>
          <GenerateTest />
        </ProtectedRoute>
      </Route>
      <Route path="/create/review-test">
        <ProtectedRoute>
          <ReviewTest />
        </ProtectedRoute>
      </Route>
      <Route path="/create/finalize">
        <ProtectedRoute>
          <FinalizeTest />
        </ProtectedRoute>
      </Route>
      <Route path="/create/share">
        <ProtectedRoute>
          <ShareTest />
        </ProtectedRoute>
      </Route>
      {/* Dedicated surface for opening / managing already-finalized tests.
          Sits OUTSIDE the /create/* workflow so the progress bar and
          TestContext draft are never involved. */}
      <Route path="/tests/:id/edit">
        <ProtectedRoute>
          <EditItems />
        </ProtectedRoute>
      </Route>
      <Route path="/tests/:id">
        <ProtectedRoute>
          <TestPreview />
        </ProtectedRoute>
      </Route>
      {import.meta.env.DEV && <Route path="/__crash" component={CrashTest} />}
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <DevModeProvider>
          <TestProvider>
            <UpgradeProvider>
              <Toaster />
              <ScrollToTop />
              <DevOptions />
              <ErrorBoundary>
                <Router />
              </ErrorBoundary>
            </UpgradeProvider>
          </TestProvider>
        </DevModeProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
